import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, keyframes, styled } from "@mui/material/styles";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";

import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import BrushRoundedIcon from "@mui/icons-material/BrushRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import HomeWorkRoundedIcon from "@mui/icons-material/HomeWorkRounded";
import ImageSearchRoundedIcon from "@mui/icons-material/ImageSearchRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import SellRoundedIcon from "@mui/icons-material/SellRounded";
import ShoppingBagRoundedIcon from "@mui/icons-material/ShoppingBagRounded";
import StraightenRoundedIcon from "@mui/icons-material/StraightenRounded";
import TravelExploreRoundedIcon from "@mui/icons-material/TravelExploreRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";

const RAW_API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "";

const SITE_NAME = "Art For My Room";
const CONTACT_EMAIL = "info@artformyroom.com";

const countryOptions = [
  { value: "GB", label: "United Kingdom" },
  { value: "AL", label: "Albania" },
  { value: "US", label: "United States" },
  { value: "IT", label: "Italy" },
  { value: "FR", label: "France" },
  { value: "DE", label: "Germany" },
  { value: "ES", label: "Spain" },
  { value: "GR", label: "Greece" },
  { value: "OTHER", label: "Other" },
];

const TURQUOISE = "#00B4A6";
const TURQUOISE_DARK = "#00796B";
const TURQUOISE_MIST = "#DDFBF8";
const TURQUOISE_PALE = "#F4FFFD";

const GOLD = "#D4AF37";
const GOLD_DARK = "#8A6500";
const GOLD_MIST = "#FFF3CF";

const BURGUNDY = "#7A1232";
const BURGUNDY_DARK = "#4A071D";
const BURGUNDY_MIST = "#FBEAF0";

const INK = "#071313";
const WHITE = "#FFFFFF";
const OFF_WHITE = "#FBFFFE";
const MUTED = "rgba(7, 19, 19, 0.66)";
const LINE = "rgba(7, 19, 19, 0.10)";

const HEADING_FONT = "'Space Grotesk', 'Inter', system-ui, sans-serif";
const BODY_FONT = "'Inter', 'Segoe UI', system-ui, sans-serif";
const MONO_FONT = "'JetBrains Mono', 'Fira Code', monospace";

const shimmer = keyframes`
  0% { transform: translateX(-120%); opacity: 0; }
  30% { opacity: 0.85; }
  100% { transform: translateX(150%); opacity: 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-7px); }
`;

const PageWrap = styled(Box)({
  minHeight: "100vh",
  background:
    `radial-gradient(circle at 8% 8%, ${alpha(TURQUOISE, 0.16)}, transparent 30%),
     radial-gradient(circle at 88% 10%, ${alpha(GOLD, 0.2)}, transparent 28%),
     radial-gradient(circle at 88% 78%, ${alpha(BURGUNDY, 0.13)}, transparent 32%),
     linear-gradient(180deg, #ffffff 0%, #f7fffd 44%, #fff7e5 100%)`,
  color: INK,
  fontFamily: BODY_FONT,
});

const Hero = styled(Paper)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: 42,
  padding: theme.spacing(3),
  color: WHITE,
  background:
    `radial-gradient(circle at 16% 14%, ${alpha(TURQUOISE, 0.28)}, transparent 34%),
     radial-gradient(circle at 84% 22%, ${alpha(GOLD, 0.2)}, transparent 30%),
     radial-gradient(circle at 90% 82%, ${alpha(BURGUNDY, 0.24)}, transparent 32%),
     linear-gradient(135deg, #061414 0%, #0A1F1D 52%, ${BURGUNDY_DARK} 100%)`,
  border: `1px solid ${alpha(WHITE, 0.14)}`,
  boxShadow: "0 34px 110px rgba(7,19,19,0.25)",
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(5),
  },
}));

const GlassCard = styled(Card)({
  borderRadius: 32,
  background: alpha(WHITE, 0.94),
  backdropFilter: "blur(18px)",
  border: `1px solid ${alpha(WHITE, 0.76)}`,
  boxShadow: "0 22px 70px rgba(0,80,74,0.10)",
  overflow: "hidden",
});

const ImageFrame = styled(Box)({
  position: "relative",
  overflow: "hidden",
  borderRadius: 34,
  background: "#071313",
  border: `1px solid ${alpha(WHITE, 0.15)}`,
  boxShadow: "0 30px 90px rgba(0,0,0,0.28)",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    width: "38%",
    background:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.16), transparent)",
    animation: `${shimmer} 5.5s ease-in-out infinite`,
    pointerEvents: "none",
  },
});

const DetailPill = styled(Paper)({
  borderRadius: 22,
  padding: "14px 16px",
  background: alpha(WHITE, 0.9),
  border: `1px solid ${LINE}`,
  height: "100%",
});

const GradientButton = styled(Button)({
  borderRadius: 999,
  textTransform: "none",
  fontWeight: 950,
  padding: "13px 24px",
  color: WHITE,
  background: `linear-gradient(135deg, ${TURQUOISE} 0%, ${GOLD} 58%, ${BURGUNDY} 100%)`,
  boxShadow: `0 16px 38px ${alpha(TURQUOISE, 0.22)}`,
  "&:hover": {
    background: `linear-gradient(135deg, ${TURQUOISE_DARK} 0%, #B99325 58%, ${BURGUNDY_DARK} 100%)`,
    transform: "translateY(-2px)",
  },
  "&.Mui-disabled": {
    background: "linear-gradient(135deg, #9bded8 0%, #e7cf79 100%)",
    color: alpha(WHITE, 0.86),
  },
});

const LightButton = styled(Button)({
  borderRadius: 999,
  textTransform: "none",
  fontWeight: 850,
  padding: "11px 20px",
  color: TURQUOISE_DARK,
  border: `1.5px solid ${alpha(TURQUOISE, 0.35)}`,
  background: alpha(WHITE, 0.86),
  "&:hover": {
    background: TURQUOISE_MIST,
    borderColor: TURQUOISE,
  },
});


const Field = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: 18,
    backgroundColor: alpha(WHITE, 0.96),
  },
  "& .MuiSelect-icon": {
    color: TURQUOISE_DARK,
  },
});

const BurgundyButton = styled(Button)({
  borderRadius: 999,
  textTransform: "none",
  fontWeight: 900,
  padding: "12px 22px",
  color: WHITE,
  background: `linear-gradient(135deg, ${BURGUNDY}, ${BURGUNDY_DARK})`,
  boxShadow: `0 14px 32px ${alpha(BURGUNDY, 0.2)}`,
  "&:hover": {
    background: `linear-gradient(135deg, ${BURGUNDY_DARK}, ${BURGUNDY})`,
    transform: "translateY(-2px)",
  },
});

function buildApiUrl(path) {
  const base = RAW_API_BASE_URL.replace(/\/+$/, "");
  const cleanPath = path.replace(/^\/+/, "");

  if (!base) {
    return `/${cleanPath.startsWith("api/") ? cleanPath : `api/${cleanPath}`}`;
  }

  if (base.endsWith("/api")) {
    return `${base}/${cleanPath.replace(/^api\//, "")}`;
  }

  return `${base}/${cleanPath.startsWith("api/") ? cleanPath : `api/${cleanPath}`}`;
}

function setOrCreateMeta(name, content, attr = "name") {
  if (!content) return;

  let tag = document.querySelector(`meta[${attr}="${name}"]`);

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
}

function setCanonical(url) {
  if (!url) return;

  let link = document.querySelector('link[rel="canonical"]');

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }

  link.setAttribute("href", url);
}

function setJsonLd(schema) {
  const id = "artwork-product-jsonld";

  let script = document.getElementById(id);

  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(schema || {}, null, 2);
}

function cleanText(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  return String(value).trim() || fallback;
}

function isNegotiableCategory(category) {
  return category === "painting" || category === "sculpture";
}

function isBuyNowOnlyCategory(category) {
  return category === "print" || category === "decorative_object";
}



function slugifyArtistName(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function getArtistPublicPath(artistName) {
  const slug = slugifyArtistName(artistName);

  if (!slug || slug === "artist" || slug === "hidden-artist") {
    return "";
  }

  return `/artist/${slug}`;
}

function categoryPathLabel(category) {
  if (category === "painting") return "Original paintings";
  if (category === "print") return "A3 prints";
  if (category === "sculpture") return "Sculptures";
  if (category === "decorative_object") return "Decorative objects";
  return "Artwork";
}

function PaletteSwatches({ palette = [] }) {
  if (!palette.length) {
    return (
      <Typography sx={{ color: MUTED, lineHeight: 1.7 }}>
        Palette information is still being prepared for this artwork.
      </Typography>
    );
  }

  return (
    <Grid container spacing={1.4}>
      {palette.slice(0, 8).map((item, index) => (
        <Grid item xs={6} sm={4} md={3} key={`${item.hex || index}-${item.name || index}`}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              border: `1px solid ${LINE}`,
              background: WHITE,
            }}
          >
            <Box
              sx={{
                height: 70,
                background: item.hex || TURQUOISE_MIST,
              }}
            />
            <Box sx={{ p: 1.25 }}>
              <Typography sx={{ fontWeight: 950, lineHeight: 1.1 }}>
                {item.name || item.hex || "colour"}
              </Typography>
              <Typography sx={{ color: MUTED, fontSize: 13 }}>
                {item.hex || "tone"} {item.percentage ? `· ${item.percentage}%` : ""}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

function DetailItem({ icon, label, value, tone = "turquoise" }) {
  const isGold = tone === "gold";
  const isBurgundy = tone === "burgundy";

  const bg = isGold ? GOLD_MIST : isBurgundy ? BURGUNDY_MIST : TURQUOISE_MIST;
  const color = isGold ? GOLD_DARK : isBurgundy ? BURGUNDY : TURQUOISE_DARK;

  return (
    <DetailPill elevation={0}>
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: "14px",
            bgcolor: bg,
            color,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: MUTED, fontSize: 12, fontWeight: 850 }}>
            {label}
          </Typography>
          <Typography sx={{ fontWeight: 950, color: INK, lineHeight: 1.2 }}>
            {value || "Not specified"}
          </Typography>
        </Box>
      </Stack>
    </DetailPill>
  );
}


function parseMoneyNumber(value) {
  const number = Number(String(value || "").replace("£", "").replace(",", "").trim());
  return Number.isFinite(number) && number > 0 ? number : null;
}

function suggestOfferAmount(value) {
  const price = parseMoneyNumber(value);

  if (!price) return "";

  return String(Math.max(1, Math.round(price * 0.9)));
}

export default function ArtworkDetailPage() {
  const { category, id } = useParams();
  const navigate = useNavigate();

  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buyLoading, setBuyLoading] = useState(false);
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  const [offerSubmitting, setOfferSubmitting] = useState(false);
  const [offerSent, setOfferSent] = useState(false);
  const [offerForm, setOfferForm] = useState({
    buyerName: "",
    buyerEmail: "",
    buyerCountry: "GB",
    amountGbp: "",
    message: "",
  });
  const [error, setError] = useState("");
  const [actionMessage, setActionMessage] = useState("");

  const endpoint = useMemo(() => {
    return buildApiUrl(`artworks/seo/${category}/${id}/`);
  }, [category, id]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadArtwork() {
      setLoading(true);
      setError("");
      setActionMessage("");

      try {
        const response = await fetch(endpoint, {
          method: "GET",
          signal: controller.signal,
        });

        let payload = {};

        try {
          payload = await response.json();
        } catch {
          payload = {};
        }

        if (!response.ok) {
          throw new Error(
            payload.message ||
              payload.detail ||
              `Artwork could not be loaded. Status ${response.status}.`
          );
        }

        setArtwork(payload);
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message || "Artwork could not be loaded.");
        }
      } finally {
        setLoading(false);
      }
    }

    loadArtwork();

    return () => controller.abort();
  }, [endpoint]);

  useEffect(() => {
    if (!artwork) return;

    const title = artwork.seo_title
      ? `${artwork.seo_title} | ${SITE_NAME}`
      : `${artwork.title} | ${SITE_NAME}`;

    document.title = title;

    setOrCreateMeta("description", artwork.seo_description || artwork.description);
    setOrCreateMeta("og:title", title, "property");
    setOrCreateMeta("og:description", artwork.seo_description || artwork.description, "property");
    setOrCreateMeta("og:type", "product", "property");
    setOrCreateMeta("og:url", artwork.canonical_url, "property");
    setOrCreateMeta("og:image", artwork.image_url, "property");
    setOrCreateMeta("twitter:card", "summary_large_image", "name");
    setOrCreateMeta("twitter:title", title, "name");
    setOrCreateMeta("twitter:description", artwork.seo_description || artwork.description, "name");
    setOrCreateMeta("twitter:image", artwork.image_url, "name");

    setCanonical(artwork.canonical_url);

    if (artwork.product_schema) {
      setJsonLd(artwork.product_schema);
    }
  }, [artwork]);

  useEffect(() => {
    if (!artwork) return;

    setOfferForm((prev) => {
      const alreadyPrepared = prev.amountGbp || prev.message;

      if (alreadyPrepared) return prev;

      return {
        ...prev,
        amountGbp: suggestOfferAmount(artwork.price_gbp),
        message: `Hello, I would like to make an offer for ${artwork.title}.`,
      };
    });
  }, [artwork]);

  const canNegotiate = artwork && isNegotiableCategory(artwork.category);
  const buyNowOnly = artwork && isBuyNowOnlyCategory(artwork.category);

  const artistPublicPath = artwork ? getArtistPublicPath(artwork.artist_name) : "";

  const linkedArtistName = artistPublicPath ? (
    <Link
      component={RouterLink}
      to={artistPublicPath}
      underline="hover"
      sx={{
        color: "inherit",
        fontWeight: 950,
        textDecorationColor: alpha(GOLD, 0.55),
        "&:hover": {
          color: GOLD,
        },
      }}
    >
      {artwork.artist_name}
    </Link>
  ) : (
    artwork?.artist_name || "Artist"
  );


  const handleOfferFieldChange = (field) => (event) => {
    setOfferForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const openMakeOfferDialog = () => {
    if (!artwork) return;

    setError("");
    setActionMessage("");
    setOfferSent(false);

    setOfferForm((prev) => ({
      ...prev,
      amountGbp: prev.amountGbp || suggestOfferAmount(artwork.price_gbp),
      message:
        prev.message ||
        `Hello, I would like to make an offer for ${artwork.title}.`,
    }));

    setOfferDialogOpen(true);
  };

  const handleSubmitOffer = async () => {
    if (!artwork) return;

    const buyerName = offerForm.buyerName.trim();
    const buyerEmail = offerForm.buyerEmail.trim();
    const amount = parseMoneyNumber(offerForm.amountGbp);

    if (!buyerName) {
      setError("Please add your name before sending the offer.");
      return;
    }

    if (!buyerEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyerEmail)) {
      setError("Please add a valid email before sending the offer.");
      return;
    }

    if (!amount) {
      setError("Please add a valid offer amount.");
      return;
    }

    setOfferSubmitting(true);
    setError("");
    setActionMessage("");

    try {
      const response = await fetch(buildApiUrl("room-offers/create/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          direct_artwork_offer: true,
          source: "artwork_seo_page",
          source_type: "actual",

          painting_id: artwork.id,
          artwork_id: artwork.id,
          item_id: artwork.id,
          category: artwork.category,
          artwork_category: artwork.category,

          room_upload_id: null,
          room_upload: null,

          buyer_name: buyerName,
          buyer_email: buyerEmail,
          buyer_country: offerForm.buyerCountry,
          amount_gbp: String(amount),
          offer_amount_gbp: String(amount),
          message: offerForm.message.trim(),

          artwork_title: artwork.title,
          artwork_page_url: artwork.canonical_url,
        }),
      });

      let payload = {};

      try {
        payload = await response.json();
      } catch {
        payload = {};
      }

      if (!response.ok) {
        throw new Error(
          payload.message ||
            payload.error ||
            payload.detail ||
            "Could not send the offer. Please try again."
        );
      }

      setOfferSent(true);
      setActionMessage(
        payload.message ||
          "Your offer has been sent to the artist. You will receive updates by email."
      );
    } catch (err) {
      setError(err.message || "Could not send the offer. Please try again.");
    } finally {
      setOfferSubmitting(false);
    }
  };

  const handleBuyNow = async () => {
    if (!artwork) return;

    setBuyLoading(true);
    setActionMessage("");
    setError("");

    try {
      const response = await fetch(buildApiUrl("buy-now/create/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          artwork_id: artwork.id,
          item_id: artwork.id,
          painting_id: artwork.id,
          category: artwork.category,
          artwork_category: artwork.category,
          source_type:
            artwork.category === "painting"
              ? "actual"
              : artwork.category === "print"
                ? "print"
                : artwork.category,
        }),
      });

      let payload = {};

      try {
        payload = await response.json();
      } catch {
        payload = {};
      }

      if (!response.ok) {
        throw new Error(
          payload.message ||
            payload.error ||
            "Could not start checkout. Please enquire by email."
        );
      }

      const checkoutUrl =
        payload.checkout_url ||
        payload.stripe_checkout_url ||
        payload.url ||
        payload.payment_url;

      if (!checkoutUrl) {
        throw new Error("Checkout link was not returned. Please enquire by email.");
      }

      window.open(checkoutUrl, "_blank", "noopener,noreferrer");
      setActionMessage("Stripe checkout opened in a new tab.");
    } catch (err) {
      setActionMessage(
        `Could not open checkout automatically. Please email ${CONTACT_EMAIL} with the artwork title.`
      );
    } finally {
      setBuyLoading(false);
    }
  };

  const handleRoomMatch = () => {
    if (!artwork) {
      navigate("/artist-room-matcher");
      return;
    }

    navigate("/artist-room-matcher", {
      state: {
        artworkId: artwork.id,
        artworkTitle: artwork.title,
        artworkCategory: artwork.category,
        artworkSlug: artwork.slug,
      },
    });
  };

  const handleMakeOffer = () => {
    openMakeOfferDialog();
  };

  const mailtoUrl = artwork
    ? `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
        `Artwork enquiry: ${artwork.title}`
      )}&body=${encodeURIComponent(
        `Hello Art For My Room,\n\nI am interested in this artwork:\n\n${artwork.title}\n${artwork.canonical_url}\n\nPlease send me more details.\n\nThank you.`
      )}`
    : `mailto:${CONTACT_EMAIL}`;

  if (loading) {
    return (
      <PageWrap>
        <Container maxWidth="lg" sx={{ py: 10 }}>
          <GlassCard>
            <CardContent sx={{ p: 5, textAlign: "center" }}>
              <CircularProgress sx={{ color: TURQUOISE_DARK }} />
              <Typography sx={{ mt: 2, fontWeight: 900 }}>
                Loading artwork page...
              </Typography>
            </CardContent>
          </GlassCard>
        </Container>
      </PageWrap>
    );
  }

  if (error || !artwork) {
    return (
      <PageWrap>
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <GlassCard>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Stack spacing={2}>
                <Button
                  component={RouterLink}
                  to="/"
                  startIcon={<ArrowBackRoundedIcon />}
                  sx={{
                    alignSelf: "flex-start",
                    textTransform: "none",
                    fontWeight: 850,
                    color: TURQUOISE_DARK,
                  }}
                >
                  Back home
                </Button>

                <Alert severity="error" sx={{ borderRadius: 3 }}>
                  {error || "Artwork could not be found."}
                </Alert>

                <Typography sx={{ color: MUTED, lineHeight: 1.7 }}>
                  This artwork page may not exist yet, or the URL may contain an incorrect ID.
                </Typography>
              </Stack>
            </CardContent>
          </GlassCard>
        </Container>
      </PageWrap>
    );
  }

  const imageAlt = cleanText(
    artwork.image_seo?.alt_text,
    `${artwork.category_label} titled ${artwork.title}`
  );

  return (
    <PageWrap>
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{
            mb: 2.5,
            "& .MuiBreadcrumbs-separator": {
              color: alpha(INK, 0.35),
            },
          }}
        >
          <Link
            component={RouterLink}
            to="/"
            underline="hover"
            sx={{ color: TURQUOISE_DARK, fontWeight: 850 }}
          >
            Home
          </Link>
          <Link
            component={RouterLink}
            to="/artist-room-matcher"
            underline="hover"
            sx={{ color: TURQUOISE_DARK, fontWeight: 850 }}
          >
            Find Art
          </Link>
          <Typography sx={{ color: MUTED, fontWeight: 800 }}>
            {categoryPathLabel(artwork.category)}
          </Typography>
        </Breadcrumbs>

        <Hero elevation={0}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <ImageFrame sx={{ animation: `${float} 7s ease-in-out infinite` }}>
                {artwork.image_url ? (
                  <Box
                    component="img"
                    src={artwork.image_url}
                    alt={imageAlt}
                    title={cleanText(artwork.image_seo?.title, artwork.title)}
                    loading="eager"
                    decoding="async"
                    sx={{
                      display: "block",
                      width: "100%",
                      maxHeight: { xs: 560, md: 720 },
                      objectFit: "contain",
                      bgcolor: "#071313",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      minHeight: 420,
                      display: "grid",
                      placeItems: "center",
                      color: alpha(WHITE, 0.7),
                    }}
                  >
                    <ImageSearchRoundedIcon sx={{ fontSize: 70 }} />
                  </Box>
                )}
              </ImageFrame>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={2.4}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip
                    icon={<VerifiedRoundedIcon />}
                    label={artwork.category_label}
                    sx={{
                      bgcolor: alpha(WHITE, 0.12),
                      color: WHITE,
                      border: `1px solid ${alpha(WHITE, 0.18)}`,
                      fontWeight: 900,
                      "& .MuiChip-icon": { color: GOLD },
                    }}
                  />

                  {artwork.for_sale && (
                    <Chip
                      icon={<CheckCircleRoundedIcon />}
                      label="Available"
                      sx={{
                        bgcolor: alpha(TURQUOISE, 0.18),
                        color: WHITE,
                        border: `1px solid ${alpha(TURQUOISE, 0.28)}`,
                        fontWeight: 900,
                        "& .MuiChip-icon": { color: TURQUOISE_MIST },
                      }}
                    />
                  )}

                  {canNegotiate && (
                    <Chip
                      icon={<LocalOfferRoundedIcon />}
                      label="Offers accepted"
                      sx={{
                        bgcolor: alpha(GOLD, 0.16),
                        color: WHITE,
                        border: `1px solid ${alpha(GOLD, 0.28)}`,
                        fontWeight: 900,
                        "& .MuiChip-icon": { color: GOLD },
                      }}
                    />
                  )}
                </Stack>

                <Box>
                  <Typography
                    component="h1"
                    sx={{
                      fontFamily: HEADING_FONT,
                      fontWeight: 950,
                      fontSize: { xs: "2.5rem", sm: "3.4rem", md: "5rem" },
                      lineHeight: 0.94,
                      letterSpacing: "-0.075em",
                      mb: 1.5,
                    }}
                  >
                    {artwork.title}
                  </Typography>

                  <Typography
                    sx={{
                      color: alpha(WHITE, 0.78),
                      fontSize: { xs: "1rem", md: "1.15rem" },
                      lineHeight: 1.8,
                      maxWidth: 720,
                    }}
                  >
                    {artwork.room_fit_description || artwork.seo_description}
                  </Typography>
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2.2,
                    borderRadius: 5,
                    bgcolor: alpha(WHITE, 0.1),
                    border: `1px solid ${alpha(WHITE, 0.14)}`,
                  }}
                >
                  <Grid container spacing={1.4}>
                    <Grid item xs={6}>
                      <Typography sx={{ color: alpha(WHITE, 0.58), fontSize: 12, fontWeight: 850 }}>
                        Artist
                      </Typography>
                      <Typography sx={{ color: WHITE, fontWeight: 950 }}>
                        {linkedArtistName}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography sx={{ color: alpha(WHITE, 0.58), fontSize: 12, fontWeight: 850 }}>
                        Price
                      </Typography>
                      <Typography sx={{ color: GOLD, fontWeight: 950 }}>
                        {artwork.price_display}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography sx={{ color: alpha(WHITE, 0.58), fontSize: 12, fontWeight: 850 }}>
                        Size
                      </Typography>
                      <Typography sx={{ color: WHITE, fontWeight: 900 }}>
                        {artwork.size || "Not specified"}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography sx={{ color: alpha(WHITE, 0.58), fontSize: 12, fontWeight: 850 }}>
                        Location
                      </Typography>
                      <Typography sx={{ color: WHITE, fontWeight: 900 }}>
                        {artwork.country || "Not specified"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {actionMessage && (
                  <Alert
                    severity={actionMessage.includes("Could not") ? "warning" : "success"}
                    sx={{ borderRadius: 3 }}
                  >
                    {actionMessage}
                  </Alert>
                )}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                  {artwork.for_sale && (
                    <GradientButton
                      startIcon={
                        buyLoading ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <ShoppingBagRoundedIcon />
                        )
                      }
                      onClick={handleBuyNow}
                      disabled={buyLoading}
                    >
                      {buyLoading ? "Opening checkout..." : "Buy now"}
                    </GradientButton>
                  )}

                  {canNegotiate && (
                    <BurgundyButton
                      startIcon={<LocalOfferRoundedIcon />}
                      onClick={handleMakeOffer}
                    >
                      Make offer
                    </BurgundyButton>
                  )}

                  {buyNowOnly && (
                    <LightButton
                      startIcon={<TravelExploreRoundedIcon />}
                      onClick={handleRoomMatch}
                      sx={{
                        color: WHITE,
                        borderColor: alpha(WHITE, 0.28),
                        bgcolor: alpha(WHITE, 0.08),
                        "&:hover": {
                          bgcolor: alpha(WHITE, 0.14),
                          borderColor: WHITE,
                        },
                      }}
                    >
                      Try in my room
                    </LightButton>
                  )}

                  <LightButton
                    component="a"
                    href={mailtoUrl}
                    startIcon={<OpenInNewRoundedIcon />}
                    sx={{
                      color: WHITE,
                      borderColor: alpha(WHITE, 0.28),
                      bgcolor: alpha(WHITE, 0.08),
                      "&:hover": {
                        bgcolor: alpha(WHITE, 0.14),
                        borderColor: WHITE,
                      },
                    }}
                  >
                    Enquire
                  </LightButton>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Hero>

        <Grid container spacing={3.5} sx={{ mt: 3.5 }}>
          <Grid item xs={12} lg={8}>
            <GlassCard>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Stack spacing={2.5}>
                  <Box>
                    <Chip
                      icon={<AutoAwesomeRoundedIcon />}
                      label="Artwork story"
                      sx={{
                        mb: 1.5,
                        bgcolor: TURQUOISE_MIST,
                        color: TURQUOISE_DARK,
                        fontWeight: 950,
                        "& .MuiChip-icon": { color: TURQUOISE_DARK },
                      }}
                    />

                    <Typography
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontWeight: 950,
                        fontSize: { xs: "1.8rem", md: "2.5rem" },
                        letterSpacing: "-0.055em",
                        lineHeight: 1,
                        mb: 1.5,
                      }}
                    >
                      Why this artwork belongs in a room
                    </Typography>

                    <Typography sx={{ color: MUTED, lineHeight: 1.85, fontSize: "1rem" }}>
                      {artwork.description}
                    </Typography>
                  </Box>

                  <Divider sx={{ borderColor: LINE }} />

                  <Box>
                    <Typography sx={{ fontWeight: 950, mb: 1.5 }}>
                      Palette and image SEO
                    </Typography>

                    <PaletteSwatches palette={artwork.palette} />
                  </Box>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 5,
                      bgcolor: OFF_WHITE,
                      border: `1px solid ${LINE}`,
                    }}
                  >
                    <Stack spacing={1}>
                      <Typography sx={{ fontWeight: 950 }}>
                        Image alt text
                      </Typography>
                      <Typography sx={{ color: MUTED, lineHeight: 1.75 }}>
                        {imageAlt}
                      </Typography>
                    </Stack>
                  </Paper>
                </Stack>
              </CardContent>
            </GlassCard>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Stack spacing={2}>
              <GlassCard>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography
                    sx={{
                      fontFamily: HEADING_FONT,
                      fontWeight: 950,
                      fontSize: "1.5rem",
                      letterSpacing: "-0.04em",
                      mb: 2,
                    }}
                  >
                    Artwork details
                  </Typography>

                  <Grid container spacing={1.4}>
                    <Grid item xs={12}>
                      <DetailItem
                        icon={<BrushRoundedIcon />}
                        label="Artist"
                        value={linkedArtistName}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <DetailItem
                        icon={<Inventory2RoundedIcon />}
                        label="Medium"
                        value={artwork.medium}
                        tone="gold"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <DetailItem
                        icon={<StraightenRoundedIcon />}
                        label="Size"
                        value={artwork.size}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <DetailItem
                        icon={<PublicRoundedIcon />}
                        label="Country"
                        value={artwork.country}
                        tone="burgundy"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <DetailItem
                        icon={<SellRoundedIcon />}
                        label="Price"
                        value={artwork.price_display}
                        tone="gold"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <DetailItem
                        icon={<CategoryRoundedIcon />}
                        label="Category"
                        value={artwork.category_label}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </GlassCard>

              <GlassCard>
                <CardContent sx={{ p: 2.5 }}>
                  <Stack spacing={1.4}>
                    <Chip
                      icon={<HomeWorkRoundedIcon />}
                      label="Room matching"
                      sx={{
                        alignSelf: "flex-start",
                        bgcolor: BURGUNDY_MIST,
                        color: BURGUNDY,
                        fontWeight: 950,
                        "& .MuiChip-icon": { color: BURGUNDY },
                      }}
                    />

                    <Typography
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontWeight: 950,
                        fontSize: "1.45rem",
                        letterSpacing: "-0.04em",
                        lineHeight: 1.05,
                      }}
                    >
                      See how it feels in your room
                    </Typography>

                    <Typography sx={{ color: MUTED, lineHeight: 1.7 }}>
                      Upload a room photo and place matched art into the space before buying.
                    </Typography>

                    <GradientButton
                      fullWidth
                      startIcon={<TravelExploreRoundedIcon />}
                      onClick={handleRoomMatch}
                    >
                      Match with my room
                    </GradientButton>

                    {artistPublicPath && (
                      <LightButton
                        component={RouterLink}
                        to={artistPublicPath}
                        fullWidth
                        startIcon={<BrushRoundedIcon />}
                      >
                        View all works by {artwork.artist_name}
                      </LightButton>
                    )}
                  </Stack>
                </CardContent>
              </GlassCard>

              <Paper
                elevation={0}
                sx={{
                  p: 2.4,
                  borderRadius: 6,
                  color: WHITE,
                  background:
                    `linear-gradient(135deg, ${TURQUOISE_DARK}, ${BURGUNDY_DARK})`,
                  boxShadow: "0 18px 46px rgba(7,19,19,0.18)",
                }}
              >
                <Stack spacing={1.3}>
                  <Typography sx={{ fontWeight: 950 }}>
                    SEO page created for discovery
                  </Typography>

                  <Typography sx={{ color: alpha(WHITE, 0.74), lineHeight: 1.7 }}>
                    This page includes image alt text, a canonical URL and Product structured data.
                  </Typography>

                  <Typography
                    sx={{
                      color: alpha(WHITE, 0.5),
                      fontFamily: MONO_FONT,
                      fontSize: 12,
                      wordBreak: "break-word",
                    }}
                  >
                    {artwork.canonical_url}
                  </Typography>
                </Stack>
              </Paper>
            </Stack>
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: { xs: 3, md: 4 },
            borderRadius: 8,
            background:
              `linear-gradient(135deg, ${alpha(TURQUOISE_MIST, 0.95)}, ${alpha(GOLD_MIST, 0.8)}, ${alpha(BURGUNDY_MIST, 0.7)})`,
            border: `1px solid ${alpha(GOLD, 0.24)}`,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography
                sx={{
                  fontFamily: HEADING_FONT,
                  fontWeight: 950,
                  fontSize: { xs: "1.9rem", md: "2.6rem" },
                  letterSpacing: "-0.055em",
                  lineHeight: 1,
                  mb: 1,
                }}
              >
                Find art that belongs in your room
              </Typography>

              <Typography sx={{ color: MUTED, lineHeight: 1.75 }}>
                Art For My Room helps buyers discover paintings, prints, sculptures and decorative objects that match real room photos.
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack spacing={1.2}>
                <GradientButton
                  fullWidth
                  startIcon={<HomeRoundedIcon />}
                  onClick={handleRoomMatch}
                >
                  Upload my room
                </GradientButton>

                <LightButton
                  fullWidth
                  component={RouterLink}
                  to="/artist-launch"
                  startIcon={<BrushRoundedIcon />}
                >
                  Upload original art
                </LightButton>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Container>

        <Dialog
          open={offerDialogOpen}
          onClose={() => {
            if (!offerSubmitting) {
              setOfferDialogOpen(false);
              setOfferSent(false);
            }
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 7,
              overflow: "hidden",
              border: `1px solid ${alpha(TURQUOISE, 0.18)}`,
            },
          }}
        >
          <DialogTitle
            sx={{
              p: 3,
              color: WHITE,
              background: `linear-gradient(135deg, ${TURQUOISE_DARK}, ${GOLD}, ${BURGUNDY})`,
            }}
          >
            <Typography
              sx={{
                fontFamily: HEADING_FONT,
                fontWeight: 950,
                fontSize: { xs: "1.7rem", sm: "2.15rem" },
                letterSpacing: "-0.055em",
                lineHeight: 1,
              }}
            >
              Make an offer
            </Typography>

            <Typography sx={{ color: alpha(WHITE, 0.82), mt: 1, lineHeight: 1.6 }}>
              {artwork?.title} · {artwork?.price_display}
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            {offerSent ? (
              <Stack spacing={2.2}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: TURQUOISE_MIST,
                    color: TURQUOISE_DARK,
                    border: `1px solid ${alpha(TURQUOISE, 0.24)}`,
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ fontSize: 40 }} />
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontFamily: HEADING_FONT,
                      fontWeight: 950,
                      fontSize: "1.65rem",
                      letterSpacing: "-0.045em",
                      lineHeight: 1,
                    }}
                  >
                    Offer sent
                  </Typography>

                  <Typography sx={{ color: MUTED, lineHeight: 1.75, mt: 1.2 }}>
                    Your offer has been sent. The artist can accept, decline or counter-offer.
                    Please check your email for updates.
                  </Typography>
                </Box>

                <GradientButton
                  fullWidth
                  onClick={() => {
                    setOfferDialogOpen(false);
                    setOfferSent(false);
                  }}
                >
                  Done
                </GradientButton>
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Alert severity="info" sx={{ borderRadius: 3 }}>
                  Make a direct offer for this artwork. No room upload is needed.
                </Alert>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Field
                      fullWidth
                      required
                      label="Your name"
                      value={offerForm.buyerName}
                      onChange={handleOfferFieldChange("buyerName")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BrushRoundedIcon sx={{ color: TURQUOISE_DARK }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Field
                      fullWidth
                      required
                      type="email"
                      label="Your email"
                      value={offerForm.buyerEmail}
                      onChange={handleOfferFieldChange("buyerEmail")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PublicRoundedIcon sx={{ color: TURQUOISE_DARK }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Field
                      fullWidth
                      select
                      label="Your country"
                      value={offerForm.buyerCountry}
                      onChange={handleOfferFieldChange("buyerCountry")}
                    >
                      {countryOptions.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Field
                      fullWidth
                      required
                      type="number"
                      label="Your offer GBP"
                      value={offerForm.amountGbp}
                      onChange={handleOfferFieldChange("amountGbp")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SellRoundedIcon sx={{ color: TURQUOISE_DARK }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      fullWidth
                      multiline
                      minRows={4}
                      label="Message to the artist"
                      value={offerForm.message}
                      onChange={handleOfferFieldChange("message")}
                    />
                  </Grid>
                </Grid>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                  <BurgundyButton
                    fullWidth
                    disabled={offerSubmitting}
                    startIcon={
                      offerSubmitting ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <LocalOfferRoundedIcon />
                      )
                    }
                    onClick={handleSubmitOffer}
                  >
                    {offerSubmitting ? "Sending offer..." : "Send offer"}
                  </BurgundyButton>

                  <LightButton
                    fullWidth
                    disabled={offerSubmitting}
                    onClick={() => setOfferDialogOpen(false)}
                  >
                    Cancel
                  </LightButton>
                </Stack>
              </Stack>
            )}
          </DialogContent>
        </Dialog>

    </PageWrap>
  );
}
